import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard, EmailGuard } from "../App";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faFileCsv, faPlus, faTrash, faMoneyBillWave, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const PropertyHunter = () => {
  const [propertyHunterData, setPropertyHunterData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [propertyHunterToDelete, setPropertyHunterToDelete] = useState(null);
  const navigate = useNavigate();

  const [newPropertyHunter, setNewPropertyHunter] = useState({
    property_id: "",
    hunterid: "",
    id_number: "",
    phone_number: "",
    email: "",
    name: "",
    percentage: "",
    balance: "",
  });

  useEffect(() => {
    fetchPropertyHunter();
  }, []);

  const fetchPropertyHunter = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${StandardURL}/iNyumba/admin/househunter/fetch`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPropertyHunterData(response.data.response || []);
      toast.success("Property hunter data fetched successfully!");
    } catch (error) {
      console.error("Error fetching property hunters:", error);
      toast.error(error.response?.data?.response || "Failed to fetch data.");
      setPropertyHunterData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPropertyHunter({ ...newPropertyHunter, [name]: value });
  };

  const handleAddPropertyHunter = async () => {
    const { property_id, hunterid, id_number, phone_number, email, name, percentage, balance } = newPropertyHunter;
    if (!property_id || !hunterid || !id_number || !phone_number || !email || !name || !percentage || !balance) {
      toast.error("All fields are required.");
      return;
    }

    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/admin/househunter/add`,
        newPropertyHunter,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success(response.data.response);
      fetchPropertyHunter();
      setNewPropertyHunter({
        property_id: "",
        hunterid: "",
        id_number: "",
        phone_number: "",
        email: "",
        name: "",
        percentage: "",
        balance: "",
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error adding PropertyHunter:", error);
      toast.error(error.response?.data?.response || "Failed to add property hunter.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleSettlePropertyHunter = (idnumber) => {
    localStorage.setItem("settle_idnumber", idnumber);
    navigate("/admin/settlepage");
  };

  const openDeleteModal = (propertyHunter) => {
    setPropertyHunterToDelete(propertyHunter);
    setIsDeleteModalOpen(true);
  };

  const handleDeletePropertyHunter = async () => {
    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(
        `${StandardURL}/iNyumba/admin/househunter/delete/${propertyHunterToDelete.propertyid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.response);
      fetchPropertyHunter();
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting property hunter:", error);
      toast.error(error.response?.data?.response || "Failed to delete property hunter.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDownload = async (fileType) => {
    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    const endpoint = `${StandardURL}/iNyumba/report/propertyhunterviewreports`;

    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { format: fileType },
        responseType: "blob",
      });
      const fileExtension = fileType === "pdf" ? "pdf" : "csv";
      const fileName = `propertyhunter_report.${fileExtension}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Download successful!");
    } catch (error) {
      console.error(`Error downloading ${fileType.toUpperCase()}:`, error);
      toast.error(error.response?.data?.response || `Failed to download ${fileType.toUpperCase()} file.`);
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Property Hunters</h1>
            <div className="flex space-x-2">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Hunter
              </button>
              <button
                onClick={() => handleDownload("csv")}
                disabled={isActionLoading}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faFileCsv} className="mr-2" /> Download CSV
              </button>
              <button
                onClick={() => handleDownload("pdf")}
                disabled={isActionLoading}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md flex items-center transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faFilePdf} className="mr-2" /> Download PDF
              </button>
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <Oval color="#4F46E5" height={50} width={50} />
              </div>
            ) : propertyHunterData.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No property hunters available.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hunter ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {propertyHunterData.map((hunter) => (
                      <tr key={hunter.propertyid}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{hunter.propertyid}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hunter.hunterid}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hunter.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hunter.idnumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hunter.phonenumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hunter.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{hunter.percentage}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Ksh {hunter.balance}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {hunter.balance > 0 && (
                            <button
                              onClick={() => handleSettlePropertyHunter(hunter.idnumber)}
                              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md transition-colors duration-200"
                            >
                              <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1" /> Settle
                            </button>
                          )}
                          <button
                            onClick={() => openDeleteModal(hunter)}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md transition-colors duration-200"
                            disabled={isActionLoading}
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" /> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Add Property Hunter Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center">
            <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-lg w-full">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">Add Property Hunter</h2>
              <div className="space-y-4">
                <input
                  type="text"
                  name="property_id"
                  value={newPropertyHunter.property_id}
                  onChange={handleInputChange}
                  placeholder="Property ID"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  name="hunterid"
                  value={newPropertyHunter.hunterid}
                  onChange={handleInputChange}
                  placeholder="Hunter ID"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  name="id_number"
                  value={newPropertyHunter.id_number}
                  onChange={handleInputChange}
                  placeholder="ID Number"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  name="phone_number"
                  value={newPropertyHunter.phone_number}
                  onChange={handleInputChange}
                  placeholder="Phone Number (e.g., 2547...)"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  name="email"
                  value={newPropertyHunter.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  name="name"
                  value={newPropertyHunter.name}
                  onChange={handleInputChange}
                  placeholder="Name"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  name="percentage"
                  value={newPropertyHunter.percentage}
                  onChange={handleInputChange}
                  placeholder="Percentage (e.g., 0.1 for 10%)"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  name="balance"
                  value={newPropertyHunter.balance}
                  onChange={handleInputChange}
                  placeholder="Balance"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end mt-6 space-x-2">
                <button
                  onClick={handleAddPropertyHunter}
                  disabled={isActionLoading}
                  className={`flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white transition-colors duration-200
                    ${isActionLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {isActionLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  )}
                  {isActionLoading ? "Adding..." : "Add Property Hunter"}
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 rounded-md font-semibold bg-gray-500 hover:bg-gray-600 text-white transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center">
            <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-sm w-full text-center">
              <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
              <p className="mb-6">Are you sure you want to delete this property hunter?</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleDeletePropertyHunter}
                  disabled={isActionLoading}
                  className={`flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white transition-colors duration-200
                    ${isActionLoading ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
                >
                  {isActionLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={faTrash} className="mr-2" />
                  )}
                  {isActionLoading ? "Deleting..." : "Delete"}
                </button>
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-6 py-3 rounded-md font-semibold bg-gray-500 hover:bg-gray-600 text-white transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </AuthGuard>
    </EmailGuard>
  );
};

export default PropertyHunter;